// Custom hooks for API data fetching and mutations
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  policiesAPI, 
  reviewsAPI, 
  // dashboardAPI,
  systemStandardsAPI,
  adminStandardsAPI,
  adminAPI
} from '../services/api';
import { useAuth } from './useAuth';

// Policies hooks
export const usePolicies = () => {
  return useQuery('policies', policiesAPI.getAll, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePolicy = (id: string) => {
  return useQuery(['policy', id], () => policiesAPI.getById(id), {
    enabled: !!id,
  });
};

export const useUploadPolicy = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ files, name }: { files: File[]; name?: string }) => 
      policiesAPI.upload(files, name),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('policies');
      },
    }
  );
};

export const useDeletePolicy = () => {
  const queryClient = useQueryClient();
  
  return useMutation(policiesAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('policies');
    },
  });
};

// Reviews hooks
export const useReviews = () => {
  return useQuery('reviews', reviewsAPI.getAll, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReview = (id: string) => {
  return useQuery(['review', id], () => reviewsAPI.getById(id), {
    enabled: !!id,
  });
};

export const useCreateReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ policyId, standardId, systemStandardId }: { 
      policyId: string; 
      standardId?: string; 
      systemStandardId?: string; 
    }) => reviewsAPI.create(policyId, standardId, systemStandardId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('reviews');
        queryClient.invalidateQueries('dashboard-stats');
      },
    }
  );
};

export const useDeleteReview = () => {
  const queryClient = useQueryClient();
  
  return useMutation(reviewsAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('reviews');
      queryClient.invalidateQueries('dashboard-stats');
    },
  });
};

export const useDownloadReviewReport = () => {
  return useMutation(reviewsAPI.downloadReport, {
    onError: (error) => {
      console.error('📄 Review report download error:', error);
    },
  });
};

export const useReviewStatus = (reviewId: string | null, options?: { enabled?: boolean; refetchInterval?: number }) => {
  return useQuery(
    ['review-status', reviewId],
    () => reviewsAPI.getStatus(reviewId!),
    {
      enabled: !!reviewId && (options?.enabled !== false),
      refetchInterval: (data: any) => {
        // Stop polling once the review reaches a terminal state
        const status = data?.status;
        if (status === 'completed' || status === 'completed_with_errors' || status === 'failed' || status === 'error') {
          return false;
        }
        // Also stop if we've received data but status is missing (unexpected response)
        if (data && !status) {
          return false;
        }
        return options?.refetchInterval || 3000;
      },
      refetchIntervalInBackground: true,
      staleTime: 1000,
      retry: 2,
      onError: (error) => {
        console.error('📊 Review status fetch error:', error);
      },
    }
  );
};

// Dashboard hooks
import { dashboardAPI } from '../services/api';
export const useDashboardStats = () => {
  return useQuery('dashboard-stats', dashboardAPI.getStats, {
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: (error) => {
      console.error('📊 Dashboard stats error:', error);
    },
  });
};

export const useAdminDashboardStats = () => {
  return useQuery('admin-dashboard-stats', adminAPI.getDashboardStats, {
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: (error) => {
      console.error('📊 Admin dashboard stats error:', error);
    },
  });
};

// System Standards hooks
export const useSystemStandards = () => {
  return useQuery('system-standards', systemStandardsAPI.getAll, {
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSystemStandard = (id: string) => {
  return useQuery(['system-standard', id], () => systemStandardsAPI.getById(id), {
    enabled: !!id,
  });
};

// Admin hooks
export { useUsers } from './useUsers';
export { useSystemStatus } from './useSystemStatus';

// Configuration hooks
export const useAppSettings = () => {
  return useQuery('app-settings', adminAPI.getAppSettings, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useIntegrations = () => {
  return useQuery('integrations', adminAPI.getIntegrations, {
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useEnvironmentInfo = () => {
  return useQuery('environment-info', adminAPI.getEnvironmentInfo, {
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Admin Standards hooks
export const useAdminStandards = () => {
  return useQuery('admin-standards', adminStandardsAPI.getAll, {
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error('📚 Admin standards error:', error);
    },
    onSuccess: (data) => {
      console.log('📚 Admin standards loaded:', data);
    }
  });
};

export const useAddAdminStandard = () => {
  const queryClient = useQueryClient();
  return useMutation(adminStandardsAPI.add, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-standards');
    },
  });
};

export const useUpdateAdminStandard = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, updates }: { id: string; updates: any }) => adminStandardsAPI.update(id, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-standards');
      },
    }
  );
};

export const useDeleteAdminStandard = () => {
  const queryClient = useQueryClient();
  return useMutation(adminStandardsAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-standards');
    },
  });
};

// Review Details hook
export const useReviewDetails = (reviewId: string | null, options?: { enabled?: boolean }) => {
  return useQuery(
    ['review-details', reviewId],
    () => reviewsAPI.getDetails(reviewId!),
    {
      enabled: Boolean(reviewId) && (options?.enabled !== false),
      onError: (error) => {
        console.error('📄 Review details fetch error:', error);
      },
    }
  );
};

// Subscription Plans Management
export const useSubscriptionPlans = () => {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const response = await adminAPI.getSubscriptionPlans();
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateSubscriptionPlan = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (planData: any) => {
      const response = await adminAPI.createSubscriptionPlan(planData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
};

export const useUpdateSubscriptionPlan = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ planId, planData }: { planId: number; planData: any }) => {
      const response = await adminAPI.updateSubscriptionPlan(planId, planData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
};

export const useDeleteSubscriptionPlan = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (planId: number) => {
      const response = await adminAPI.deleteSubscriptionPlan(planId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });
};

// Usage Analytics
export const useUsageOverview = () => {
  return useQuery({
    queryKey: ['usage-overview'],
    queryFn: async () => {
      const response = await adminAPI.getUsageOverview();
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useUserUsage = () => {
  return useQuery({
    queryKey: ['user-usage'],
    queryFn: async () => {
      const response = await adminAPI.getUserUsage();
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Revenue Analytics
export const useRevenueAnalytics = (period: string = '30d') => {
  return useQuery({
    queryKey: ['revenue-analytics', period],
    queryFn: async () => {
      return await adminAPI.getRevenueAnalytics(period);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUsageAnalytics = (period: string = '30d') => {
  return useQuery({
    queryKey: ['usage-analytics', period],
    queryFn: async () => {
      return await adminAPI.getUsageAnalytics(period);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Top Users Analytics
export const useTopUsers = (period: string = '30d') => {
  return useQuery({
    queryKey: ['top-users', period],
    queryFn: async () => {
      return await adminAPI.getTopUsers(period);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Performance Metrics
export const usePerformanceMetrics = () => {
  return useQuery({
    queryKey: ['performance-metrics'],
    queryFn: async () => {
      return await adminAPI.getPerformanceMetrics();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// --- Security & Compliance API Hooks ---

export const useAuditLogs = (params?: {
  user_id?: number;
  action?: string;
  resource_type?: string;
  start_date?: string;
  end_date?: string;
  success?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: async () => {
      return await adminAPI.getAuditLogs(params);
    },
    enabled: !!user?.is_superuser,
  });
};

export const useAuditStatistics = (params?: {
  start_date?: string;
  end_date?: string;
}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['audit-statistics', params],
    queryFn: async () => {
      return await adminAPI.getAuditStatistics(params);
    },
    enabled: !!user?.is_superuser,
  });
};

export const useComplianceDashboard = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['compliance-dashboard'],
    queryFn: async () => {
      return await adminAPI.getComplianceDashboard();
    },
    enabled: !!user?.is_superuser,
  });
};

export const useSecurityIncidents = (params?: {
  start_date?: string;
  end_date?: string;
  severity?: string;
}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['security-incidents', params],
    queryFn: async () => {
      return await adminAPI.getSecurityIncidents(params);
    },
    enabled: !!user?.is_superuser,
  });
};

export const useAccessControlStatus = () => {
  return useQuery('access-control-status', adminAPI.getAccessControlStatus);
};

export const useSecurityStatus = () => {
  return useQuery('security-status', adminAPI.getSecurityStatus);
};

export const useSOC2Report = (params?: {
  report_type?: string;
  start_date?: string;
  end_date?: string;
}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['soc2-report', params],
    queryFn: async () => {
      return await adminAPI.getSOC2Report(params);
    },
    enabled: !!user?.is_superuser,
  });
};

// --- Security & Compliance Mutations ---

export const useExportAuditLogs = () => {
  return useMutation({
    mutationFn: async (params: {
      format?: string;
      start_date?: string;
      end_date?: string;
    }) => {
      return await adminAPI.exportAuditLogs(params);
    },
  });
};

export const useExportUserData = () => {
  return useMutation({
    mutationFn: async (params: {
      user_id: number;
      format?: string;
    }) => {
      return await adminAPI.exportUserData(params.user_id, params.format);
    },
  });
};

export const useDeleteUserData = () => {
  return useMutation({
    mutationFn: async (params: {
      user_id: number;
      reason?: string;
    }) => {
      return await adminAPI.deleteUserData(params.user_id, params.reason);
    },
  });
};

export const useCleanupExpiredData = () => {
  return useMutation({
    mutationFn: async (params: {
      data_type?: string;
    }) => {
      return await adminAPI.cleanupExpiredData(params.data_type);
    },
  });
};

export const useGetRetentionReport = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['retention-report'],
    queryFn: async () => {
      return await adminAPI.getRetentionReport();
    },
    enabled: !!user?.is_superuser,
  });
};

export const useRunSecurityAssessment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await adminAPI.runSecurityAssessment();
    },
    onSuccess: () => {
      queryClient.invalidateQueries('compliance-dashboard');
      queryClient.invalidateQueries('security-status');
      queryClient.invalidateQueries('access-control-status');
      queryClient.invalidateQueries('security-incidents');
      queryClient.invalidateQueries('audit-logs');
      queryClient.invalidateQueries('audit-statistics');
    },
  });
};
